import { useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import { changePassword } from "../api/auth";
import { ApiError } from "../api/client";
import { friendlyErrorMessage } from "../api/errors";
import { TextField } from "../components/TextField";
import { Button } from "../components/Button";
import { StatusBanner } from "../components/StatusBanner";

const MIN_LENGTH = 8;

export function ChangePasswordPage() {
  const { state, login, expireSession } = useAuth();
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // operation_id é gerado UMA vez e reaproveitado em qualquer retry (idempotência do backend — ver
  // auth-change-password). Só é trocado quando o próprio servidor diz que a tentativa anterior falhou
  // de vez (operation_failed_previously) e pede um id novo.
  const operationIdRef = useRef(crypto.randomUUID());

  if (state.status !== "authenticated") {
    // ProtectedRoute já garante isso, mas TypeScript precisa do narrowing aqui.
    return null;
  }
  const { accessToken, username } = state;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setFormError(null);

    if (newPassword.length < MIN_LENGTH) {
      setFormError(`A nova senha precisa ter pelo menos ${MIN_LENGTH} caracteres.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setFormError("As senhas não coincidem.");
      return;
    }

    setLoading(true);
    try {
      await changePassword(accessToken, newPassword, operationIdRef.current);
      // A troca de senha pelo Auth pode invalidar o access token temporário. Reautenticar aqui
      // instala a sessão nova antes de liberar o restante do portal.
      await login(username, newPassword);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        expireSession();
        navigate("/login", { replace: true });
        return;
      }
      if (err instanceof ApiError && err.code === "operation_failed_previously") {
        operationIdRef.current = crypto.randomUUID();
      }
      setError(friendlyErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="centered-page">
      <div className="card">
        <h1>Troque sua senha</h1>
        <p className="card__subtitle">
          {state.requiresPasswordChange
            ? "Sua senha atual é temporária. Defina uma senha definitiva para continuar."
            : "Atualize sua senha quando desejar manter suas credenciais seguras."}
        </p>

        {!isOnline && <StatusBanner tone="warning">Sem conexão com a internet no momento.</StatusBanner>}
        {error && <StatusBanner tone="error">{error}</StatusBanner>}

        <form onSubmit={handleSubmit} noValidate>
          <TextField
            label="Nova senha"
            name="new-password"
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_LENGTH}
            hint={`Pelo menos ${MIN_LENGTH} caracteres.`}
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
          />
          <TextField
            label="Confirmar nova senha"
            name="confirm-password"
            type="password"
            autoComplete="new-password"
            required
            error={formError ?? undefined}
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
          />
          <Button type="submit" loading={loading} disabled={!isOnline}>
            Salvar nova senha
          </Button>
        </form>
      </div>
    </div>
  );
}
